import * as Bowser from 'bowser'
import classnames from 'classnames'
import { DropdownItem } from 'embed/constants'
import { css } from 'emotion'
import * as React from 'react'
import Select from 'react-select'

import { useTheme } from 'embed/hooks'

const style = require('./styles.scss')

export interface Props {
  containerClass?: string
  currentValue?: DropdownItem<unknown>
  fieldName?: string
  isDisabled?: boolean
  items?: DropdownItem<unknown>[]
  label?: string
  onChange?: (fieldName: string, value: string, isValid: boolean) => void
  required?: boolean
  startingValue?: string | number
  styles?: FormStyles
  menuStyle?: unknown | undefined
  scrollMenuIntoView?: boolean
}

export interface FormStyles {
  dropDown?: string
  dropDownLabel?: string
  fieldError?: string
}

export interface State {
  currentValue?: DropdownItem<unknown>
  isValid: boolean
}

const FormDropdown: React.FC<Props> = ({
  containerClass,
  fieldName,
  isDisabled,
  items,
  label,
  menuStyle,
  onChange,
  required,
  scrollMenuIntoView,
  startingValue,
  styles,
  ...props
}) => {
  const startingItem = items?.find((item) => item.value === startingValue)
  const [currentValue, setCurrentValue] = React.useState(props.currentValue || startingItem || items?.[0])
  const [isValid, setIsValid] = React.useState(true)
  const inputNode: React.MutableRefObject<Select | null> = React.useRef(null)

  React.useEffect(() => {
    if (props.currentValue !== undefined && props.currentValue !== currentValue) {
      setCurrentValue(props.currentValue)
    }
  }, [props.currentValue])

  const formatWrapperClass = React.useCallback(() => [isValid ? '' : styles?.fieldError].join(' '), [
    isValid,
    styles?.fieldError,
  ])

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsValid(!required || (currentValue !== undefined && currentValue.value !== ''))
    },
    [required, currentValue],
  )

  const handleFocus = React.useCallback(() => {
    if (Bowser.mobile) {
      inputNode.current?.input.blur()
    }
  }, [inputNode.current?.input.blur])

  const handleClose = React.useCallback(() => {
    if (Bowser.mobile) {
      inputNode.current?.setState({ isFocused: false, isPseudoFocused: false })
    }
  }, [inputNode.current])

  const handleChange = React.useCallback(
    (selectedOption) => {
      const newValue = selectedOption === null ? { value: '', label: '' } : selectedOption
      const hasDifferentValue = currentValue !== newValue

      if (hasDifferentValue) {
        setCurrentValue(newValue)
        if (fieldName !== undefined) {
          onChange?.(fieldName, newValue.value === '' ? undefined : newValue.value, true)
        }
      }
    },
    [currentValue, fieldName, onChange],
  )

  const { borderRadiusSmall, fontSizeBase, labelStyles } = useTheme()
  return (
    <div className={containerClass}>
      <div className={styles?.dropDownLabel} style={labelStyles}>
        {label}
      </div>
      <div className={formatWrapperClass()}>
        {/* We're temporarily disabling searchability for all FormDropdown components because of a WebView
            bug. See MPS-112. */}
        <Select
          className={classnames(
            styles?.dropDown,
            css`
              font-size: ${fontSizeBase};
              .Select-control {
                border-radius: ${borderRadiusSmall};
              }
            `,
          )}
          disabled={isDisabled}
          name={fieldName}
          value={currentValue}
          menuStyle={menuStyle}
          onBlur={handleBlur}
          onChange={handleChange}
          onClose={handleClose}
          onFocus={handleFocus}
          options={items}
          removeSelected={false}
          clearable={false}
          ref={inputNode}
          scrollMenuIntoView={scrollMenuIntoView}
          searchable={false}
        />
      </div>
    </div>
  )
}

export default FormDropdown
